import React, { useMemo } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useTranslation } from "react-i18next";
import { GoogleG, GoogleWordmark } from "../GoogleBrand/GoogleBrand";

const GOOGLE_REVIEWS_URL =
  "https://www.google.com/maps/place/BEST+GROOM+STUDIO+Nina+Vorona/@51.083752,17.038425,19z/data=!3m1!4b1!4m6!3m5!1s0x470fc2f7c44f30b1:0xfa473c5008e1f423!8m2!3d51.0836649!4d17.0383929!16s%2Fg%2F11f01qx71q";

const GoogleMapsReviews: React.FC = (): JSX.Element => {
  const { t } = useTranslation();

  interface Review {
    name: string;
    rating: number;
    text: string;
    profilePhotoUrl: string;
  }

  const createReview = (
    name: string,
    rating: number,
    textKey: string,
    profilePhotoUrl: string
  ): Review => ({
    name,
    rating,
    text: t(textKey),
    profilePhotoUrl,
  });

  const reviews = [
    createReview(
      "Magdalena Piprek",
      5,
      "reviewsSection.review1",
      "/reviews/magdalena.jpg"
    ),
    createReview(
      "Robert Ch.",
      4,
      "reviewsSection.review2",
      "/reviews/robert.jpg"
    ),
    createReview(
      "Anna Klodowska",
      5,
      "reviewsSection.review3",
      "/reviews/anna.jpg"
    ),
    createReview(
      "Pedro Augusto",
      5,
      "reviewsSection.review4",
      "/reviews/pedro.jpg"
    ),
    createReview(
      "Grzegorz Joniec",
      5,
      "reviewsSection.review5",
      "/reviews/grzegorz.jpg"
    ),
  ];

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const sliderSettings = useMemo(
    () => ({
      dots: true,
      infinite: !prefersReducedMotion,
      speed: prefersReducedMotion ? 0 : 500,
      slidesToShow: 3,
      slidesToScroll: 1,
      arrows: false,
      autoplay: !prefersReducedMotion,
      autoplaySpeed: 4000,
      pauseOnHover: true,
      adaptiveHeight: false,
      swipe: true,
      cssEase: "ease-in-out",
      accessibility: true,
      focusOnSelect: false,
      responsive: [
        {
          breakpoint: 1920,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 480,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
          },
        },
      ],
    }),
    [prefersReducedMotion]
  );

  const handleImageError = (e: { currentTarget: HTMLImageElement }): void => {
    const target = e.currentTarget;
    const name = target.alt.replace(/'s profile$/, "");
    target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
      name
    )}&size=128&rounded=true`;
    target.onerror = null;
  };

  return (
    <section
      className="mx-auto max-w-6xl py-8"
      aria-label="Customer Reviews"
      id="reviewsId"
    >
      <div className="mb-10 text-center">
        <span className="section-eyebrow">{t("reviews")}</span>
        <h2 className="section-title">{t("reviewsSection.title")}</h2>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-sm text-gray-600">
          <span
            className="relative inline-block text-lg leading-none tracking-[2px] text-gray-300"
            aria-hidden="true"
          >
            <span
              className="absolute left-0 top-0 overflow-hidden whitespace-nowrap text-amber-400"
              style={{ width: `${(4.7 / 5) * 100}%` }}
            >
              ★★★★★
            </span>
            ★★★★★
          </span>
          <span>
            <strong className="text-gray-800">4.7</strong> · 225+{" "}
            {t("header.googleReviews")}
          </span>
          <span className="inline-flex items-center gap-1">
            <GoogleG size={16} />
            <GoogleWordmark className="text-base" />
          </span>
        </div>
      </div>
      <div className="px-4 md:px-8">
        <Slider {...sliderSettings}>
          {reviews.map((review, index) => (
            <div
              key={index}
              className="h-full p-3"
              role="group"
              aria-label={`Review ${index + 1} of ${reviews.length}`}
            >
              <div className="relative mx-auto flex h-full max-w-sm flex-col items-center rounded-2xl bg-white p-8 text-center shadow-sm ring-1 ring-black/5 transition-shadow duration-300 hover:shadow-lg">
                <GoogleG size={20} className="absolute right-5 top-5" />
                <img
                  src={review.profilePhotoUrl}
                  alt={`${review.name}'s profile`}
                  width={64}
                  height={64}
                  className="mb-4 size-16 rounded-full object-cover ring-2 ring-mint ring-offset-2"
                  loading="lazy"
                  onError={handleImageError}
                />
                <h3 className="mb-1 text-lg font-semibold text-gray-800">
                  {review.name}
                </h3>
                <div
                  className="mb-4 text-base tracking-wider"
                  role="img"
                  aria-label={`Rating: ${review.rating} out of 5 stars`}
                >
                  {[...Array(5)].map((_, i) => (
                    <span
                      key={i}
                      className={
                        i < review.rating ? "text-amber-400" : "text-gray-200"
                      }
                      aria-hidden="true"
                    >
                      ★
                    </span>
                  ))}
                </div>
                <p className="text-sm italic leading-relaxed text-gray-600">
                  &ldquo;{review.text}&rdquo;
                </p>
              </div>
            </div>
          ))}
        </Slider>
      </div>
      <div className="mt-10 text-center">
        <a
          href={GOOGLE_REVIEWS_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 font-medium text-primary-deep underline-offset-4 transition-colors hover:text-primary hover:underline"
        >
          <GoogleG size={16} />
          {t("reviewsSection.seeAll")}
          <span aria-hidden="true">→</span>
        </a>
      </div>
    </section>
  );
};

export default GoogleMapsReviews;
